import { useCallback } from 'react';
import { useLocalSearchParams } from 'expo-router';

import { useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useNavigationReset } from '../../../routing';

/**
 * Returns a helper function that redirects to the corresponding screen provided
 * as a route for `Auth` related screens.
 */
export const useRedirectAfterAuthStateChange = () => {
  const resetNavigation = useNavigationReset();
  const { minWidth } = useResponsive();
  const {
    redirect: redirectToPath = 'home',
    redirectToGiftCardId,
    redirectToReceiptId,
  } = useLocalSearchParams<{
    redirect?: string;
    redirectToGiftCardId?: string;
    redirectToReceiptId?: string;
  }>();

  const isReceiptEnabled = useFeatureFlag('cels-4664-enable-receipt-screen');

  // ─────────────────────────────────────────────────────────────────────

  return useCallback(() => {
    // Guard: gift-redeem requires a gift card ID.
    if (redirectToPath === 'gift-redeem' && !redirectToGiftCardId) return;

    // Guard: receipt redirects require a receipt ID + feature flag.
    if (RECEIPT_REDIRECTS.has(redirectToPath) && (!redirectToReceiptId || !isReceiptEnabled))
      return;

    const resetState = getResetState(
      redirectToPath,
      redirectToGiftCardId,
      redirectToReceiptId,
      minWidth.isSM,
    );

    resetNavigation(resetState);
  }, [
    isReceiptEnabled,
    resetNavigation,
    minWidth.isSM,
    redirectToPath,
    redirectToGiftCardId,
    redirectToReceiptId,
  ]);
};

// ─── Reset Maps ──────────────────────────────────────────────────────────────

const TAB_ROUTE_MAP: Record<string, TabRouteMapping> = {
  home: { tabName: '(home)' },
  locations: { tabName: '(menu)', screenName: 'locations/index' },
  profile: { tabName: 'account', screenName: 'info/index' },
  orders: { tabName: 'account', screenName: 'orders/index' },
  addresses: { tabName: 'account', screenName: 'addresses/index' },
  favorites: { tabName: 'account', screenName: 'favorites/index' },
  'payment-and-gift-cards': { tabName: 'account', screenName: 'payment-and-gift-cards/index' },
  'credit-and-promo-codes': { tabName: 'account', screenName: 'credit-and-promo-codes/index' },
  'dietary-restrictions': { tabName: 'account', screenName: 'dietary-restrictions/index' },
  notifications: { tabName: 'account', screenName: 'notifications/index' },
  'gift-cards': { tabName: 'gift' },
  'gift-redeem': { tabName: 'gift', screenName: 'redeem/[giftCardId]/index' },
};

const MODAL_ROUTE_NAMES: Record<string, string> = {
  bag: 'bag/index',
  'add-gift-card': 'add-gift-card/index',
  payments: 'payments/index',
  receipt: 'receipt/[receiptId]/index',
  'receipt-claim': 'receipt/claim/[receiptId]/index',
};

const SM_MODAL_TAB_OVERRIDES: Record<string, Required<TabRouteMapping>> = {
  'add-gift-card': { tabName: 'account', screenName: 'gift-cards/index' },
  payments: { tabName: 'account', screenName: 'payment-and-gift-cards/index' },
};

const GIFT_CARD_REDIRECTS = new Set(['payment-and-gift-cards', 'add-gift-card', 'gift-redeem']);
const RECEIPT_REDIRECTS = new Set(['receipt', 'receipt-claim']);

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Builds the reset state for redirects after auth state change. Resets the
 * root stack to exclude `(auth)` and land on the correct target screen.
 */
function getResetState(
  redirectToPath: string,
  giftCardId: string | undefined,
  receiptId: string | undefined,
  isSmUp: boolean,
): ResetState {
  const params = getRedirectParams(redirectToPath, giftCardId, receiptId);

  // On SM+, redirect modal screens to their tab equivalents.
  const smTabOverride = isSmUp ? SM_MODAL_TAB_OVERRIDES[redirectToPath] : undefined;

  if (smTabOverride) {
    const screenRoute = params
      ? { name: smTabOverride.screenName, params }
      : { name: smTabOverride.screenName };

    return {
      index: 0,
      routes: [
        {
          name: '(tabs)',
          state: { routes: [{ name: smTabOverride.tabName, state: { routes: [screenRoute] } }] },
        },
      ],
    };
  }

  const modalRouteName = MODAL_ROUTE_NAMES[redirectToPath];

  if (modalRouteName) {
    const modalRoute = params ? { name: modalRouteName, params } : { name: modalRouteName };

    return {
      index: 1,
      routes: [
        { name: '(tabs)', state: { routes: [{ name: '(home)' }] } },
        { name: '(modals)', state: { routes: [modalRoute] } },
      ],
    };
  }

  const { tabName, screenName } = TAB_ROUTE_MAP[redirectToPath] ?? TAB_ROUTE_MAP.home;

  if (!screenName) {
    return {
      index: 0,
      routes: [{ name: '(tabs)', state: { routes: [{ name: tabName }] } }],
    };
  }

  const screenRoute = params ? { name: screenName, params } : { name: screenName };

  return {
    index: 0,
    routes: [
      {
        name: '(tabs)',
        state: { routes: [{ name: tabName, state: { routes: [screenRoute] } }] },
      },
    ],
  };
}

function getRedirectParams(
  redirectToPath: string,
  redirectToGiftCardId: string | undefined,
  redirectToReceiptId: string | undefined,
): Record<string, string> | undefined {
  if (GIFT_CARD_REDIRECTS.has(redirectToPath) && redirectToGiftCardId) {
    return { giftCardId: redirectToGiftCardId };
  }
  if (RECEIPT_REDIRECTS.has(redirectToPath) && redirectToReceiptId) {
    return { receiptId: redirectToReceiptId };
  }
  return undefined;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ResetState = Parameters<ReturnType<typeof useNavigationReset>>[0];

type TabRouteMapping = {
  tabName: string;
  screenName?: string;
};
